import { useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/useAuth";
import { deletionMutationToken, deletionReadToken } from "@/auth/deletionAuthentication";
import { createDeletionCommandRunner } from "@/api/deletionCommand";
import { AdminApiError } from "@/api/client";
import type { DeletionPage } from "@/api/accountDeletion";
import { env } from "@/config/env";

/** Explicit private reads; request status remains a Firestore subscription. */
export function useDeletionPages<T>(
  resource: string[],
  fetchPage: (
    token: string,
    cursor: string | undefined,
    signal: AbortSignal,
  ) => Promise<DeletionPage<T>>,
  enabled = true,
) {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const query = useInfiniteQuery({
    queryKey: ["account-deletion", env.firebase.projectId, uid, ...resource],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      fetchPage(await deletionReadToken(uid), pageParam, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!uid && enabled,
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return {
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    loading: enabled && (query.isPending || query.isFetching),
    error: query.error,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      if (!query.isFetching) void query.fetchNextPage();
    },
    refresh: () => query.refetch(),
  };
}

export function useDeletionCommand() {
  const { user, capabilities } = useAuth();
  const uid = user?.uid ?? "";
  const runner = useMemo(() => createDeletionCommandRunner(uid), [uid]);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const canAct =
    capabilities.act && capabilities.actDestructive && (!env.isProd || capabilities.actProd);

  async function execute<T>(
    target: string,
    updatedAt: string,
    input: object,
    send: (token: string, clientRequestId: string) => Promise<T>,
  ): Promise<T> {
    if (!uid || !canAct) throw new Error("Your role cannot record this decision.");
    if (inFlight.current) throw new Error("An action is already in progress.");
    inFlight.current = true;
    setPending(true);
    try {
      return await runner(
        { actor: uid, target, updatedAt, input },
        () => deletionMutationToken(uid),
        send,
      );
    } catch (error) {
      if (error instanceof AdminApiError && error.conflict) {
        throw new Error(
          "This case changed or this action was already used. Refresh, review the latest details, and reopen the action.",
        );
      }
      throw error;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  return { execute, pending, canAct };
}
